import type { FeedbackType, PageMetadata, ProjectField, Severity } from '@feedback/shared';
// Deep import on purpose: the root export pulls in zod, which has no business in
// a script tag on someone else's page. This module is pure and dependency-free.
import { resolvePathHintValues, type PathHint } from '@feedback/shared/path-hints';
import { el, icons, svg } from './dom.js';
import { createAnnotator, type Annotator, type Tool } from './annotator.js';

export interface PanelCallbacks {
  onSubmit(input: PanelSubmission): Promise<{ issueUrl: string; issueNumber: number }>;
  onRetake(): Promise<void>;
  onClose(): void;
}

export interface PanelSubmission {
  title: string;
  description: string;
  type: FeedbackType;
  severity: Severity;
  reporterName: string;
  reporterEmail: string;
  screenshot: string | undefined;
  /** Values for the project's declared fields; empty when it declares none. */
  fieldValues: Record<string, string>;
}

export interface PanelOptions {
  /**
   * Identity supplied by the host app via `identify()`, used to prefill the
   * reporter fields.
   */
  reporterDefaults?: StoredReporter;
  /** Extra inputs this project declares. Empty renders the form unchanged. */
  fields?: readonly ProjectField[];
  /** Preselects those fields from the page path. Empty preselects nothing. */
  pathHints?: readonly PathHint[];
}

export interface Panel {
  element: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  showCapturing(): void;
  showScreenshot(image: HTMLImageElement, warnings: string[]): void;
  showCaptureFailed(message: string): void;
  setMetadata(metadata: PageMetadata): void;
}

const STORAGE_KEY = 'fb-widget-reporter';

export interface StoredReporter {
  name?: string;
  email?: string;
}

function loadReporter(): StoredReporter {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredReporter) : {};
  } catch {
    return {};
  }
}

function saveReporter(reporter: StoredReporter): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reporter));
  } catch {
    // Private-mode browsers reject writes; remembering the name is optional.
  }
}

const TOOLS: Array<{ tool: Tool; label: string; icon: string }> = [
  { tool: 'rect', label: 'Box', icon: icons.rect },
  { tool: 'arrow', label: 'Arrow', icon: icons.arrow },
  { tool: 'pen', label: 'Draw', icon: icons.pen },
  { tool: 'blackout', label: 'Hide', icon: icons.blackout },
];

const TYPES: Array<{ value: FeedbackType; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'question', label: 'Question' },
];

export function createPanel(
  callbacks: PanelCallbacks,
  accent: string,
  options: PanelOptions = {},
): Panel {
  const { reporterDefaults, fields = [], pathHints = [] } = options;

  let annotator: Annotator | null = null;
  let metadata: PageMetadata | null = null;
  let open = false;

  /* ------------------------------------------------------------- preview */

  const stage = el('div', { class: 'preview__stage' });

  const toolButtons = new Map<Tool, HTMLButtonElement>();
  const toolbar = el('div', { class: 'toolbar', role: 'toolbar', 'aria-label': 'Annotation tools' });

  for (const { tool, label, icon } of TOOLS) {
    const button = el('button', {
      class: 'tool',
      type: 'button',
      'aria-pressed': tool === 'rect' ? 'true' : 'false',
      title: tool === 'blackout' ? 'Hide sensitive content' : label,
    }, svg(icon, 14), label);

    button.addEventListener('click', () => selectTool(tool));
    toolButtons.set(tool, button);
    toolbar.append(button);
  }

  const undoButton = el('button', {
    class: 'tool', type: 'button', title: 'Undo last annotation', disabled: true,
  }, svg(icons.undo, 14), 'Undo');
  undoButton.addEventListener('click', () => {
    annotator?.undo();
  });

  const retakeButton = el('button', { class: 'tool', type: 'button' }, 'Retake');
  retakeButton.addEventListener('click', () => {
    void callbacks.onRetake();
  });

  toolbar.append(el('span', { class: 'toolbar__spacer' }), undoButton, retakeButton);

  function selectTool(tool: Tool): void {
    annotator?.setTool(tool);
    for (const [key, button] of toolButtons) {
      button.setAttribute('aria-pressed', key === tool ? 'true' : 'false');
    }
  }

  const preview = el('div', { class: 'preview' }, toolbar, stage);

  /* ---------------------------------------------------------------- form */

  const stored = loadReporter();
  // Identity supplied by the host app via identify() wins over the remembered
  // localStorage value: it reflects the currently signed-in user, whereas the
  // stored value may be whoever last used this browser.
  const prefill: StoredReporter = {
    name: reporterDefaults?.name ?? stored.name,
    email: reporterDefaults?.email ?? stored.email,
  };

  const titleInput = el('input', {
    class: 'input', type: 'text', maxlength: 200, required: true,
    placeholder: 'Short summary of the problem',
  });

  const descriptionInput = el('textarea', {
    class: 'textarea', maxlength: 10000,
    placeholder: 'What did you expect, and what happened instead?',
  });

  let selectedType: FeedbackType = 'bug';
  const typeButtons = new Map<FeedbackType, HTMLButtonElement>();
  const typeGroup = el('div', { class: 'segmented', role: 'group', 'aria-label': 'Feedback type' });
  for (const { value, label } of TYPES) {
    const button = el('button', {
      class: 'segmented__option', type: 'button',
      'aria-pressed': value === selectedType ? 'true' : 'false',
    }, label);
    button.addEventListener('click', () => {
      selectedType = value;
      for (const [key, other] of typeButtons) {
        other.setAttribute('aria-pressed', key === value ? 'true' : 'false');
      }
    });
    typeButtons.set(value, button);
    typeGroup.append(button);
  }

  const severitySelect = el('select', { class: 'select' });
  for (const [value, label] of [
    ['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['critical', 'Critical'],
  ] as const) {
    severitySelect.append(el('option', { value, ...(value === 'medium' ? { selected: true } : {}) }, label));
  }

  const nameInput = el('input', {
    class: 'input', type: 'text', maxlength: 120, placeholder: 'Your name',
    ...(prefill.name ? { value: prefill.name } : {}),
  });
  const emailInput = el('input', {
    class: 'input', type: 'email', maxlength: 200, placeholder: 'you@example.com',
    ...(prefill.email ? { value: prefill.email } : {}),
  });

  /*
   * Project-declared fields. They sit directly under Type because they are
   * routing metadata — the same question as "what kind of report is this?" — and
   * because a reporter who scrolls past them has already started writing prose.
   *
   * A project that declares none produces an empty `fieldRows`, so the form below
   * is assembled exactly as it was before this existed.
   */
  const fieldControls = new Map<string, HTMLSelectElement | HTMLInputElement>();
  /** Fields the reporter has changed. A hint must never overwrite one of these. */
  const touched = new Set<string>();

  function buildField(field: ProjectField): HTMLElement {
    let control: HTMLSelectElement | HTMLInputElement;

    if (field.type === 'select') {
      const select = el('select', { class: 'select' });
      // A leading blank keeps "no answer" reachable, and stops the first option
      // becoming everyone's answer by default.
      select.append(el('option', { value: '' }, field.required ? 'Choose one…' : 'Not specified'));
      for (const option of field.options ?? []) {
        select.append(el('option', { value: option }, option));
      }
      control = select;
    } else {
      control = el('input', {
        class: 'input',
        type: 'text',
        maxlength: 500,
        ...(field.placeholder ? { placeholder: field.placeholder } : {}),
      });
    }

    // Both events, because a select reports 'change' and a text input 'input'.
    // Once this fires the value is the reporter's, including a deliberate blank.
    control.addEventListener('input', () => touched.add(field.key));
    control.addEventListener('change', () => touched.add(field.key));

    fieldControls.set(field.key, control);

    return el('div', { class: 'field', 'data-field': field.key },
      el('label', { class: 'field__label' }, field.required ? `${field.label} *` : field.label),
      control,
    );
  }

  const fieldRows = fields.map(buildField);

  /**
   * Preselects fields from the current path. Called for the panel's initial state
   * and again when it closes, which starts the next report — never on open, on a
   * retake, or on any other re-render, so a value the reporter set stays set.
   *
   * The pathname is read here rather than captured once: a single-page app can
   * navigate between two reports, and the second one should reflect where they
   * actually are.
   */
  function applyPathHints(): void {
    if (fieldRows.length === 0 || pathHints.length === 0) return;

    const values = resolvePathHintValues(location.pathname, pathHints);
    for (const field of fields) {
      const value = values[field.key];
      if (value === undefined || touched.has(field.key)) continue;
      const control = fieldControls.get(field.key);
      if (control) control.value = value;
    }
  }

  applyPathHints();

  function collectFieldValues(): Record<string, string> {
    const values: Record<string, string> = {};
    for (const field of fields) {
      const value = fieldControls.get(field.key)?.value.trim();
      if (value) values[field.key] = value;
    }
    return values;
  }

  const noticeArea = el('div');
  const metaSummary = el('div', { class: 'meta-summary' });

  const submitButton = el('button', { class: 'button button--primary', type: 'submit' }, 'Create issue');
  const cancelButton = el('button', { class: 'button button--ghost', type: 'button' }, 'Cancel');
  cancelButton.addEventListener('click', () => callbacks.onClose());

  const form = el('form', { class: 'form', novalidate: true },
    el('div', { class: 'field' },
      el('label', { class: 'field__label' }, 'Type'),
      typeGroup,
    ),
    ...fieldRows,
    el('div', { class: 'field' },
      el('label', { class: 'field__label' }, 'Summary'),
      titleInput,
    ),
    el('div', { class: 'field' },
      el('label', { class: 'field__label' }, 'Details'),
      descriptionInput,
    ),
    el('div', { class: 'row' },
      el('div', { class: 'field' },
        el('label', { class: 'field__label' }, 'Severity'),
        severitySelect,
      ),
      el('div', { class: 'field' },
        el('label', { class: 'field__label' }, 'Your name'),
        nameInput,
      ),
    ),
    el('div', { class: 'field' },
      el('label', { class: 'field__label' }, 'Email (optional)'),
      emailInput,
    ),
    noticeArea,
    metaSummary,
    el('div', { class: 'form__footer' }, submitButton, cancelButton),
  );

  function setNotice(kind: 'warn' | 'error' | null, message?: string): void {
    noticeArea.replaceChildren();
    if (!kind || !message) return;
    noticeArea.append(el('div', { class: `notice notice--${kind}` }, message));
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void handleSubmit();
  });

  async function handleSubmit(): Promise<void> {
    const title = titleInput.value.trim();
    if (title.length < 3) {
      setNotice('error', 'Please add a short summary (at least 3 characters).');
      titleInput.focus();
      return;
    }

    const fieldValues = collectFieldValues();
    const missing = fields.find((field) => field.required && !fieldValues[field.key]);
    if (missing) {
      setNotice('error', `${missing.label} is required.`);
      fieldControls.get(missing.key)?.focus();
      return;
    }

    submitButton.disabled = true;
    submitButton.replaceChildren(el('span', { class: 'spinner' }), 'Creating…');
    setNotice(null);

    saveReporter({ name: nameInput.value.trim(), email: emailInput.value.trim() });

    try {
      const result = await callbacks.onSubmit({
        title,
        description: descriptionInput.value.trim(),
        type: selectedType,
        severity: severitySelect.value as Severity,
        reporterName: nameInput.value.trim(),
        reporterEmail: emailInput.value.trim(),
        screenshot: annotator?.toDataUrl(),
        fieldValues,
      });
      showSuccess(result.issueUrl, result.issueNumber);
    } catch (error) {
      submitButton.disabled = false;
      submitButton.replaceChildren(document.createTextNode('Create issue'));
      setNotice('error', error instanceof Error ? error.message : 'Something went wrong.');
    }
  }

  /* -------------------------------------------------------------- chrome */

  const closeButton = el('button', {
    class: 'icon-button', type: 'button', 'aria-label': 'Close feedback',
  }, svg(icons.close, 16));
  closeButton.addEventListener('click', () => callbacks.onClose());

  const header = el('div', { class: 'dialog__header' },
    el('div', {},
      el('h2', { class: 'dialog__title' }, 'Send feedback'),
      el('p', { class: 'dialog__subtitle' }, 'Annotate the screenshot, then describe the problem.'),
    ),
    closeButton,
  );

  const body = el('div', { class: 'dialog__body' }, preview, form);
  const dialog = el('div', {
    class: 'dialog', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Send feedback',
  }, header, body);

  const backdrop = el('div', { class: 'backdrop', hidden: true }, dialog);

  // Clicking the backdrop closes, but only when the press started there — a drag
  // that ends outside the canvas must not dismiss the reporter's work.
  backdrop.addEventListener('pointerdown', (event) => {
    if (event.target === backdrop) {
      backdrop.addEventListener('pointerup', function once(up) {
        backdrop.removeEventListener('pointerup', once);
        if (up.target === backdrop) callbacks.onClose();
      });
    }
  });

  function showSuccess(issueUrl: string, issueNumber: number): void {
    // The reporting frame is full-screen so the screenshot can be annotated; a
    // short confirmation in that frame reads as a line of text stranded in a
    // wall of white. Collapse to a small centered card (undone on close), and
    // drop the reporting header with it — the success card is self-contained.
    dialog.classList.add('dialog--done');
    body.replaceChildren(
      el('div', { class: 'success' },
        el('div', { class: 'success__badge' }, svg(icons.check, 24)),
        el('h3', { class: 'success__title' }, 'Thanks — your feedback is filed'),
        el('p', {},
          'Created as ',
          el('a', {
            class: 'success__link', href: issueUrl, target: '_blank', rel: 'noopener noreferrer',
          }, `issue #${issueNumber}`),
          '.',
        ),
        (() => {
          const done = el('button', { class: 'button button--primary', type: 'button' }, 'Done');
          done.addEventListener('click', () => callbacks.onClose());
          return done;
        })(),
      ),
    );
  }

  function renderMetaSummary(): void {
    if (!metadata) {
      metaSummary.replaceChildren();
      return;
    }
    const browser = metadata.browser
      ? `${metadata.browser.name} ${metadata.browser.version.split('.')[0]}`
      : 'Unknown browser';
    const os = metadata.os ? metadata.os.name : 'Unknown OS';

    metaSummary.replaceChildren(
      document.createTextNode('Attached automatically: '),
      el('b', {}, `${metadata.viewport.width}×${metadata.viewport.height}`),
      document.createTextNode(' viewport · '),
      el('b', {}, browser),
      document.createTextNode(' on '),
      el('b', {}, os),
      document.createTextNode(' · console, errors and failed requests.'),
    );
  }

  return {
    element: backdrop,

    open() {
      open = true;
      backdrop.hidden = false;
      titleInput.focus();
    },

    close() {
      open = false;
      backdrop.hidden = true;
      annotator?.destroy();
      annotator = null;
      // Reset for the next report, but keep whatever the reporter typed about
      // themselves — they will be the same person next time.
      titleInput.value = '';
      descriptionInput.value = '';
      // A closed panel means that report is over, so the next one starts from the
      // hints again rather than from whatever the last reporter chose.
      for (const control of fieldControls.values()) control.value = '';
      touched.clear();
      applyPathHints();
      setNotice(null);
      submitButton.disabled = false;
      submitButton.replaceChildren(document.createTextNode('Create issue'));
      dialog.classList.remove('dialog--done');
      if (!body.contains(preview)) body.replaceChildren(preview, form);
    },

    isOpen: () => open,

    showCapturing() {
      stage.replaceChildren(
        el('div', { class: 'preview__status' }, el('span', { class: 'spinner' }), 'Capturing the page…'),
      );
      undoButton.disabled = true;
    },

    showScreenshot(image, warnings) {
      annotator?.destroy();
      annotator = createAnnotator({
        image,
        accent,
        onChange: () => {
          undoButton.disabled = !annotator?.hasEdits();
        },
      });
      selectTool('rect');
      stage.replaceChildren(annotator.canvas);
      undoButton.disabled = true;
      setNotice(warnings.length > 0 ? 'warn' : null, warnings.join(' '));
    },

    showCaptureFailed(message) {
      annotator?.destroy();
      annotator = null;
      stage.replaceChildren(el('div', { class: 'preview__status' }, message));
      undoButton.disabled = true;
      setNotice('warn', 'The screenshot could not be captured, but you can still send the report.');
    },

    setMetadata(next) {
      metadata = next;
      renderMetaSummary();
    },
  };
}
